import { Box, IconButton, Typography, Tooltip, Button } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { SummaryList } from '@lendica/components';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { currencyFormatter, dateFormatter, formatProductName } from '@lendica/utils';
import { ArrowBackIos } from '@mui/icons-material';
import { Payment, DESCRIPTION_CODE_LABEL } from '@lendica/utils';

const mapTransactionToSummary = (transaction: Payment | undefined) => {
    return [
        {
            label: 'Transaction ID',
            value: transaction ? transaction.id.substring(0, 8) : '',
        },
        {
            label: 'Deal ID',
            value: transaction ? transaction.deal.substring(0, 8) : '',
        },
        {
            label: 'Date',
            value: transaction ? dateFormatter(transaction.event_date) : '',
        },
        {
            label: 'Amount',
            value: transaction ? currencyFormatter(transaction.check_amount) : '',
        },
        {
            label: 'Product',
            value: transaction ? formatProductName(transaction.product_name ?? '') : '',
        },
        {
            label: 'Payment Direction',
            value: transaction ? transaction.payment_direction : '',
        },
        {
            label: 'Status',
            value: transaction ? transaction.resulting_status : '',
        },
        {
            label: 'Payment Method',
            value: transaction ? transaction.payment_method : '',
        },
        {
            label: 'Description Code',
            value: transaction ? DESCRIPTION_CODE_LABEL[transaction.description_code] : '',
        },
    ];
};

const convertToCSV = (transaction: Payment | undefined) => {
    const replacer = function (key: string, value: string | number) {
        return value === null ? '' : value;
    };
    let csv = 'data:text/csv;charset=utf-8,';
    let transactionSummary = mapTransactionToSummary(transaction);

    let headers = transactionSummary.map(a => a.label).join(',') + '\r\n';
    csv += headers;
    csv += transactionSummary.map((x: any) => JSON.stringify(x.value, replacer)).join(',') + '\r\n';

    const encodedUri = encodeURI(csv);
    let link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Lendica_Transaction_Summary_${transaction!.id}.csv`);
    document.body.appendChild(link);

    link.click();
};

interface TransactionSummaryViewProps {
    payments: Payment[];
}

export const TransactionSummaryView = ({ payments }: TransactionSummaryViewProps) => {
    const { transactionId } = useParams();
    const tx = payments.find(p => p.id === transactionId);
    const navigate = useNavigate();

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    flexBasis: 600,
                    height: '100%',
                    bgcolor: 'inherit',
                    p: 2,
                }}
            >
                <SummaryList
                    title={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                onClick={() => navigate('/activity-center')}
                                startIcon={<ArrowBackIos />}
                            >
                                Back
                            </Button>
                            <Typography variant="subtitle1">Transaction</Typography>
                            <Tooltip title="Download as CSV">
                                <IconButton
                                    color="secondary"
                                    aria-label="download report"
                                    size="small"
                                    onClick={() => convertToCSV(tx!)}
                                    disabled={!tx}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                    data={mapTransactionToSummary(tx)}
                />
                <Box pb={6} />
            </Box>
        </Box>
    );
};
